import { Injectable } from '@angular/core';
import { createEffect, Actions, ofType } from '@ngrx/effects';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { map, tap, switchMap, catchError } from 'rxjs/operators';
import { of } from 'rxjs';
import * as AppActions from './app.actions';
import * as AuthActions from '../../auth/+store/auth.actions';
import { APIService } from '../../services/api/api-service';
import { BrandService } from '../../services/brand.service';
import { notificationMessages } from '@care-portals/data';
import { UserService } from '../../services/api/user.service-client';

@Injectable()
export class AppEffects {
  constructor(
    private readonly actions$: Actions,
    private readonly notificationService: NzNotificationService,
    private readonly api: APIService,
    private readonly brandService: BrandService,
    private readonly userService: UserService
  ) {}

  loadApp$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.loadApp),
      switchMap(() => {
        if (window.location.host === 'mandos.portals.care') {
          const currentUrl = window.location.pathname + window.location.search;
          window.location.href = `https://brond.portals.care${currentUrl}`;
          return [];
        }
        if (window.location.host === 'joyfulrx.portals.care') {
          const currentUrl = window.location.pathname + window.location.search;
          window.location.href = `https://enovone.portals.care${currentUrl}`;
          return [];
        }
        return [AppActions.loadAppLayout()];
      })
    )
  );

  loadLayout$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.loadAppLayout),
      switchMap(action =>
        this.api.getAppLayout().pipe(
          map(layout => {
            if (!layout)
              return AppActions.loadAppLayoutFailure({
                error: 'Layout Missing'
              });

            for (let country in layout.availableStates)
              layout.availableStates[country].sort((a, b) => {
                if (a.code == 'OTHER') return 1;
                else if (b.code == 'OTHER') {
                  return -1;
                } else {
                  return a.code.localeCompare(b.code);
                }
              });

            return AppActions.loadAppLayoutSuccess({ layout });
          }),
          catchError(error => {
            console.error('Error', error);
            this.notificationService.error(
              'Error',
              'There was an error loading the app.'
            );
            return of(AppActions.loadAppLayoutFailure({ error }));
          })
        )
      )
    )
  );

  loadLayoutSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.loadAppLayoutSuccess),
      tap(({ layout }) => this.brandService.setBrand(layout)),
      tap(({ layout }) => {
        this.brandService.setupGtm(layout.gtm?.id, layout.gtm?.url);
      }),
      map(() => AuthActions.validateAuth())
    )
  );

  goToStripeDashboard$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.goToStripeDashboard),
      switchMap(action =>
        this.userService
          .getStripeDashboardUrl()
          .pipe(
            map(url => AppActions.goToStripeDashboardSuccess({ url })),
            catchError(error => {
              console.error('Error', error);
              return of(AppActions.goToStripeDashboardFailure(error));
            })
          )
      )
    )
  );

  goToStripeDashboardSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AppActions.goToStripeDashboardSuccess),
        tap(({ url }) => {
          window.open(url, '_self');
        })
      ),
    { dispatch: false }
  );

  goToStripeDashboardFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AppActions.goToStripeDashboardFailure),
        tap(({ error }) => {
          console.error(error);
          this.notificationService.error(
            notificationMessages.PortalNotAvailable.title,
            notificationMessages.PortalNotAvailable.message
          );
        })
      ),
    { dispatch: false }
  );
}
